import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraActionList, SpecraBadge, SpecraButton, SpecraEmptyState, SpecraIconButton, SpecraInlineBanner, SpecraLink, SpecraSectionHeader, SpecraSpinner, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Unlink } from 'lucide-react';
import { useState } from 'react';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { registryHref } from '../registryHref';
import { AddProofDialog } from './AddProofDialog';
import { useCapabilityLinkMutations, useCapabilityProofs } from './hooks';
import { proofKindLabel } from './proofKinds';
export function CapabilityProofSection({ capabilityId }) {
    const query = useCapabilityProofs(capabilityId);
    const { removeProof } = useCapabilityLinkMutations();
    const [open, setOpen] = useState(false);
    const canLink = query.data?.canLink === true && !query.isError;
    const proofs = query.data?.proofs ?? [];
    return /*#__PURE__*/ _jsxs("section", {
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Proof",
                action: canLink && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    size: "xs",
                    "data-testid": Testids.registryCapabilities.linkProof,
                    onClick: ()=>setOpen(true),
                    children: "Link document as proof"
                })
            }),
            query.isPending ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                label: "Loading proof"
            }) : query.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: ()=>void query.refetch(),
                    children: "Retry loading proof"
                }),
                children: "Could not load proof."
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    proofs.length ? /*#__PURE__*/ _jsx(SpecraActionList, {
                        renderLink: renderRouterLink,
                        items: proofs.map((p)=>({
                                id: p.id,
                                label: /*#__PURE__*/ _jsx(SpecraLink, {
                                    href: registryHref({
                                        kind: 'ARTIFACT',
                                        id: p.artifactId
                                    }) ?? undefined,
                                    renderLink: renderRouterLink,
                                    children: p.title
                                }),
                                eyebrow: /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: p.asPrime ? 'success' : 'neutral',
                                    children: p.asPrime ? 'As prime' : 'Not as prime'
                                }),
                                description: [
                                    proofKindLabel(p.proofKind),
                                    p.program?.name || p.contract?.name,
                                    `linked by ${p.linkedByName || 'Unknown'}`,
                                    p.linkedAt?.toDate().toLocaleDateString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                        timeZone: 'UTC'
                                    })
                                ].filter(Boolean).join(' · '),
                                actions: canLink ? /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    label: `Unlink ${p.title}`,
                                    variant: "ghost",
                                    size: "sm",
                                    "data-testid": Testids.registryCapabilities.unlink,
                                    disabled: removeProof.isPending,
                                    onClick: ()=>removeProof.mutate(p.id),
                                    children: /*#__PURE__*/ _jsx(Unlink, {})
                                }) : undefined
                            }))
                    }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        size: "compact",
                        title: "No proof linked yet."
                    }),
                    query.data?.gap === 'NO_PROOF_AS_PRIME' && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "warning",
                        variant: "framed",
                        children: "None of this proof comes from work we primed."
                    })
                ]
            }),
            removeProof.isError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: "Could not unlink proof. Try again using Unlink."
            }),
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Gaps"
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                children: query.isPending ? 'Checking proof gaps…' : query.isError ? 'Proof gaps unavailable until proof is loaded.' : query.data?.gap === 'NO_PROOF_AS_PRIME' ? 'No proof as prime. Link proof from work we primed to close this gap.' : proofs.length ? 'No prime-proof gap in the proof you can see.' : 'No proof linked yet. Link a document to establish proof.'
            }),
            open && canLink && /*#__PURE__*/ _jsx(AddProofDialog, {
                open: true,
                onOpenChange: setOpen,
                capabilityId: capabilityId
            })
        ]
    });
}
